// Shared business logic for the simple-checkout flow. Owns form state,
// validation, the three-step submit pipeline (anonymous customer → confirm
// card → submit contract), and surfaces error/success/submitting flags.
// Used by both the Next page and the embed bundle, so this MUST stay free
// of urql, Next, and @vgn-medien-holding/* imports.
import { FormEvent, useRef, useState } from 'react';
// Split card inputs (number / expiry / CVC) instead of the all-in-one
// CardElement so each field fits its own viewport on mobile — the combined
// element squeezes everything into one row and pushes CVC out of frame on
// iPhone. Stripe auto-resolves expiry + CVC from the Elements instance, so
// we only need to hand CardNumberElement to confirmCardSetup.
import { CardNumberElement, useElements, useStripe } from '@stripe/react-stripe-js';
import type { StripeCardNumberElement } from '@stripe/stripe-js';
import { createContract, simpleCreate } from './api';
import type { ContractSuccess, FormValues, SimpleCreateResponse } from './types';

// localStorage cache so accidentally closing the modal mid-checkout does not
// wipe everything the user typed. Card details are never touched (Stripe
// Element owns them). Versioned key lets us invalidate old shapes if
// FormValues ever changes.
const STORAGE_VERSION = 'v1';
const storageKey = (portal: string) => `vgn-checkout-${portal}-${STORAGE_VERSION}`;

function loadPersisted(portal: string, fallback: FormValues): FormValues {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(storageKey(portal));
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as Partial<FormValues>;
    // Strip `country` from anything we ever read back — even old blobs
    // written by previous versions of this code. AT is always the
    // starting point on modal open; buyers who genuinely need DE/CH
    // re-pick it deliberately and see the price update with the change.
    // (Persisting the country across sessions made buyers think they had
    // already chosen Austria when in fact the price reflected a country
    // they'd toggled to weeks ago.)
    const { country: _droppedCountry, ...rest } = parsed;
    return { ...fallback, ...rest };
  } catch {
    return fallback;
  }
}

function savePersisted(portal: string, values: FormValues): void {
  if (typeof window === 'undefined') return;
  try {
    // Don't write `country` to the persisted blob — see loadPersisted for
    // the rationale. The in-memory `values.country` still drives the
    // modal's current state + the reprice flow; we just don't carry it
    // across sessions.
    const { country: _droppedCountry, ...rest } = values;
    window.localStorage.setItem(storageKey(portal), JSON.stringify(rest));
  } catch {
    // Quota / private-mode: silently drop persistence.
  }
}

function clearPersisted(portal: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(storageKey(portal));
  } catch {
    // ignore
  }
}

// Consumer-side helper: lets the embed wrapper decide whether to confirm
// before closing the modal without needing access to the hook's state.
export function hasPersistedCheckoutData(portal = 'woman'): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.localStorage.getItem(storageKey(portal));
  } catch {
    return false;
  }
}

export type UseSimpleCheckoutOptions = {
  // Resolved Compax offering ID + marketing code that this checkout is bound to.
  offeringId: string;
  marketingCode: string;
  // '' for same-origin (Next page), 'https://www.woman.at' for cross-origin (embed).
  apiBase?: string;
  // Forwarded to customerCreateAnonymous; defaults to 'woman'.
  portal?: string;
  // Lifecycle callbacks for the consumer to react to terminal states.
  onSuccess?: (result: ContractSuccess) => void;
  onError?: (error: Error) => void;
  // Promo code the buyer applied (already checked); sent with the order.
  promotionalCode?: string | null;
};

const initialValues: FormValues = {
  salutation: null,
  firstName: '',
  lastName: '',
  email: '',
  birthday: '',
  street: '',
  houseNumber: '',
  zip: '',
  city: '',
  country: 'AT',
  // Marketing wants invoice as the first / default option; user can flip
  // to STRIPE in the modal to pay by credit card.
  paymentMethod: 'INVOICE',
  hasSeparateBillingAddress: false,
  billingSalutation: null,
  billingFirstName: '',
  billingLastName: '',
  billingStreet: '',
  billingHouseNumber: '',
  billingZip: '',
  billingCity: '',
  billingCountry: 'AT',
  terms: false,
  marketing: false,
};

export function useSimpleCheckout(opts: UseSimpleCheckoutOptions) {
  const stripe = useStripe();
  const elements = useElements();

  const portal = opts.portal ?? 'woman';
  const [values, setValuesState] = useState<FormValues>(() => loadPersisted(portal, initialValues));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // Set when the server could not tell whether the order went through. The
  // buyer must not submit again in that state: Compax creates a second
  // contract for every submission.
  const [locked, setLocked] = useState(false);
  // Customer (and confirmed card) of a failed attempt in this session. A retry
  // reuses them instead of creating a second customer and card setup, and the
  // server checks that customer for an existing order before submitting.
  const attemptRef = useRef<{
    created: Extract<SimpleCreateResponse, { success: true }>;
    paymentMethodId?: string;
  } | null>(null);

  const setValue = <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
    setValuesState((prev) => {
      const next = { ...prev, [field]: value };
      savePersisted(portal, next);
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (locked) return;
    setError(null);

    const { salutation, paymentMethod } = values;
    if (!salutation) {
      setError('Bitte wählen Sie eine Anrede.');
      return;
    }
    if (values.hasSeparateBillingAddress && !values.billingSalutation) {
      setError('Bitte wählen Sie eine Anrede für die Rechnungsadresse.');
      return;
    }
    if (!values.terms) {
      setError('Bitte bestätigen Sie die AGB und Datenschutzerklärung.');
      return;
    }
    // Stripe is only required when the user picked credit card. For invoice
    // we skip the SetupIntent confirmation entirely — no card, no 3DS, just
    // straight to createContract with paymentMethod: INVOICE.
    let card: StripeCardNumberElement | null = null;
    if (paymentMethod === 'STRIPE') {
      if (!stripe || !elements) {
        setError('Zahlungssystem wird noch geladen. Bitte kurz warten.');
        return;
      }
      card = elements.getElement(CardNumberElement);
      if (!card) {
        setError('Kreditkartenfeld nicht bereit.');
        return;
      }
    }

    const apiBase = opts.apiBase ?? '';
    const trimmed = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      // Already ISO YYYY-MM-DD from <input type="date">; the trim is purely
      // a safety net for hand-edited persisted values. Empty string when
      // the buyer left the field blank.
      birthday: values.birthday.trim(),
      street: values.street.trim(),
      houseNumber: values.houseNumber.trim(),
      zip: values.zip.trim(),
      city: values.city.trim(),
    };

    setSubmitting(true);
    try {
      // 1. Anonymous Compax customer (no email) + UUser placeholder → access_token,
      //    + Stripe customer + SetupIntent. PostalCode is required because Compax's
      //    userFromCustomer endpoint validates it as part of the placeholder UUser
      //    creation (the postalCode just needs to exist; we use the customer's real one).
      const previous = attemptRef.current;
      const createdResult = previous
        ? previous.created
        : await simpleCreate(apiBase, {
            firstName: trimmed.firstName,
            lastName: trimmed.lastName,
            salutation,
            postalCode: trimmed.zip,
            city: trimmed.city,
            street: trimmed.street,
            houseNumber: trimmed.houseNumber,
            portal,
            email: trimmed.email,
            marketing: values.marketing,
          });

      if (!createdResult.success) {
        setError(createdResult.error || 'Anonyme Kundenanlage fehlgeschlagen.');
        setSubmitting(false);
        return;
      }
      const created = createdResult;
      if (!previous) attemptRef.current = { created };

      // 2. Stripe card setup — only when the user picked credit card. For
      //    invoice we skip this entirely (no SetupIntent confirmation, no
      //    3DS challenge). The clientSecret created in step 1 simply goes
      //    unused; harmless on the Stripe side.
      let paymentMethodId: string | undefined = attemptRef.current?.paymentMethodId;
      if (paymentMethod === 'STRIPE' && !paymentMethodId) {
        const { error: stripeError, setupIntent } = await stripe!.confirmCardSetup(created.clientSecret, {
          payment_method: {
            card: card!,
            billing_details: {
              name: `${trimmed.firstName} ${trimmed.lastName}`.trim(),
              email: trimmed.email,
              address: {
                line1: `${trimmed.street} ${trimmed.houseNumber}`.trim(),
                postal_code: trimmed.zip,
                city: trimmed.city,
                country: values.country,
              },
            },
          },
        });
        if (stripeError || !setupIntent?.payment_method) {
          setError(stripeError?.message || 'Kartenverifizierung fehlgeschlagen.');
          setSubmitting(false);
          return;
        }
        paymentMethodId =
          typeof setupIntent.payment_method === 'string' ? setupIntent.payment_method : setupIntent.payment_method.id;
        if (attemptRef.current) attemptRef.current.paymentMethodId = paymentMethodId;
      }

      // 3. Submit the contract — basket addresses + payment method, plus
      // email attached to the previously-anonymous Compax customer via
      // contactPersons[0] inside the (extended) customerBasket mutation.
      // loginIsRealEmail propagates from step 1 (api-compax tells us whether
      // it used the real email or a placeholder as the UUser login) so the
      // server can fire a password-reset email only for brand-new users.
      const billing =
        values.hasSeparateBillingAddress && values.billingSalutation
          ? {
              salutation: values.billingSalutation,
              firstName: values.billingFirstName.trim(),
              lastName: values.billingLastName.trim(),
              street: values.billingStreet.trim(),
              houseNumber: values.billingHouseNumber.trim(),
              zip: values.billingZip.trim(),
              city: values.billingCity.trim(),
              country: values.billingCountry,
            }
          : undefined;

      const contract = await createContract(apiBase, created.accessToken, {
        offeringId: opts.offeringId,
        marketingCode: opts.marketingCode,
        email: trimmed.email,
        // Empty string → server-side helper omits from the Compax write.
        birthday: trimmed.birthday || undefined,
        firstName: trimmed.firstName,
        lastName: trimmed.lastName,
        salutation,
        street: trimmed.street,
        houseNumber: trimmed.houseNumber,
        zip: trimmed.zip,
        city: trimmed.city,
        country: values.country,
        billing,
        paymentMethod,
        ...(paymentMethod === 'STRIPE' ? { paymentMethodId, stripeCustomerId: created.stripeCustomerId } : {}),
        loginIsRealEmail: created.loginIsRealEmail,
        marketing: values.marketing,
        retry: Boolean(previous),
        promotionalCode: opts.promotionalCode || undefined,
      });

      if (!contract.success) {
        if (contract.status === 'unknown') setLocked(true);
        setError(contract.error || 'Bestellung konnte nicht abgeschlossen werden.');
        setSubmitting(false);
        return;
      }

      attemptRef.current = null;
      setSuccess(true);
      clearPersisted(portal);
      opts.onSuccess?.({
        basketId: contract.basketId,
        basketUuid: contract.basketUuid,
        signUpId: contract.signUpId,
      });
    } catch (err) {
      console.error('[SimpleCheckout] Submit failed:', err);
      // With a customer already created, the request may have died on the way
      // while the server finished. A retry is still safe: it reuses that
      // customer and the server checks for an existing order first.
      const message = attemptRef.current
        ? 'Die Verbindung wurde unterbrochen. Bitte versuchen Sie es noch einmal.'
        : err instanceof Error
          ? err.message
          : 'Ein unerwarteter Fehler ist aufgetreten.';
      setError(message);
      opts.onError?.(err instanceof Error ? err : new Error(String(err)));
      setSubmitting(false);
    }
  };

  return {
    values,
    setValue,
    submitting,
    error,
    success,
    // Consumers can disable their submit button on this; true once Stripe is ready.
    locked,
    canSubmit: Boolean(stripe && elements && !submitting && !locked),
    handleSubmit,
  };
}
